// eslint-disable-next-line import/no-restricted-paths
import { Guest } from "@CHILLNN-Inc/chillnn-driver-reservation-react";
import { memo, useCallback, useMemo, useState } from "react";
import { generatePath, useNavigate } from "react-router";

import { PATHS } from "@/routes/paths";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";

import type { ButtonProps } from "@/components/ui/button/button";
import { Button } from "@/components/ui/button/button";

// eslint-disable-next-line import/order, import/no-restricted-paths
import { AuthWrapper } from "@/lib/aws-amplify";

type Props = {
	hotelID: string;
	isTestReservationAvailable: boolean;
};

const uri = import.meta.env.VITE_GRAPHQL_API_ENDPOINT_RESERVATION_TEST;

export const TestReservation = memo(({ hotelID, isTestReservationAvailable }: Props) => {
	const canReserve = useMemo(() => isTestReservationAvailable, [isTestReservationAvailable]);
	const buttonVariant = useMemo<ButtonProps["variant"]>(() => {
		if (canReserve) {
			return "outline";
		}

		return "disabled";
	}, [canReserve]);
	const buttonTooltip = useMemo(() => {
		if (!isTestReservationAvailable) {
			return "施設設定、ホテルポリシー設定、部屋設定、サイトコントローラー設定の設定をしてください";
		}

		return "テスト予約では決済は行われませんが、在庫が連動して変化します。動作確認後は予約のキャンセルをしてください。";
	}, [isTestReservationAvailable]);

	const [open, setOpen] = useState(false);
	const onOpen = useCallback(() => setOpen(true), []);
	const onClose = useCallback(() => setOpen(false), []);

	const handleClickButton = useCallback(() => {
		onOpen();
	}, [onOpen]);

	const initializeContext = useMemo<Guest.ReservationComponent.Test.Props["initializeContext"]>(() => {
		return {
			sessionStateContext: {
				hotelID,
				lang: Guest.LangCode.Default,
			},
		};
	}, [hotelID]);

	const navigate = useNavigate();

	const handleHide = useCallback(() => {
		onClose();
	}, [onClose]);

	const handleSuccess = useCallback(
		(input: { hotelID: string; reservationID: string }) => {
			notifyUserEventInfo({
				variant: "default",
				title: "テスト予約が完了しました",
			});

			const reservationDetailPagePath = generatePath(PATHS.HOTEL.MANAGEMENT.RESERVATION.DETAIL, {
				hotelID: input.hotelID,
				reservationID: input.reservationID,
			});

			navigate(reservationDetailPagePath, { replace: true });
		},
		[navigate],
	);

	const callbackContext = useMemo<Guest.ReservationComponent.Test.Props["callbackContext"]>(() => {
		return {
			onHideCallback: handleHide,
			onSuccessCallback: handleSuccess,
		};
	}, [handleHide, handleSuccess]);

	const libContext = useMemo<Guest.ReservationComponent.Test.Props["libContext"]>(() => {
		return {
			apolloClient: {
				uri,
				getToken: async () => (await AuthWrapper.getIdToken()) ?? "",
			},
		};
	}, []);

	const envContext = useMemo<Guest.ReservationComponent.Test.Props["envContext"]>(() => {
		return {
			stage: import.meta.env.VITE_STAGE,
			user: "host",
		};
	}, []);

	return (
		<div>
			<Button type="button" variant={buttonVariant} onClick={handleClickButton} tooltip={buttonTooltip}>
				テスト予約を行う
			</Button>

			{open && (
				<Guest.ReservationComponent.Test.Presenter
					initializeContext={initializeContext}
					callbackContext={callbackContext}
					libContext={libContext}
					envContext={envContext}
				/>
			)}
		</div>
	);
});

TestReservation.displayName = "TestReservation";
